import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import * as d3 from 'd3';
import './SvgAdCanvas.scss';

interface AdElement {
  id: string;
  type: 'text' | 'image' | 'rect';
  x: number;
  y: number;
  width: number; 
  height: number;
  rotation: number;
  content?: string;
  fontSize?: number;
  fontFamily?: string;
  fill?: string;
  imageUrl?: string;
  textAlign?: 'left' | 'center' | 'right';
}

interface SvgAdCanvasProps {
  width: number; 
  height: number;
  backgroundImage: string | null;
  elements: AdElement[];
  selectedElementId: string | null;
  onElementUpdate: (id: string, updates: Partial<AdElement>) => void;
  onElementSelect: (id: string) => void;
  onTextAlignChange?: (id: string, align: 'left' | 'center' | 'right') => void;
}

export interface SvgAdCanvasHandle {
  exportAdGroup: () => Promise<Blob>;
}

const SvgAdCanvas = forwardRef<SvgAdCanvasHandle, SvgAdCanvasProps>(({
  width,
  height,
  backgroundImage,
  elements,
  selectedElementId,
  onElementUpdate,
  onElementSelect,
  onTextAlignChange
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  
  // Fixed canvas size
  const canvasWidth = 1600;
  const canvasHeight = 900;

  // Expose export function to parent via ref
  useImperativeHandle(ref, () => ({
    exportAdGroup: async () => {
      if (!svgRef.current) throw new Error('SVG not ready');
      
      const svg = d3.select(svgRef.current);
      const adGroup = svg.select('g.ad-group');
      
      if (adGroup.empty()) throw new Error('Ad group not found');
      
      // Create a temporary SVG with just the ad-group content
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.setAttribute('width', String(width));
      tempSvg.setAttribute('height', String(height));
      tempSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      
      // Clone the ad-group node and its children
      const adGroupNode = adGroup.node() as SVGGElement;
      if (!adGroupNode) throw new Error('Ad group node not found');
      
      const clonedAdGroup = adGroupNode.cloneNode(true) as SVGGElement;
      
      // Convert any image elements to data URLs to avoid CORS issues
      const imageElements = clonedAdGroup.querySelectorAll('image');
      const imagePromises = Array.from(imageElements).map(async (imgElement) => {
        const href = imgElement.getAttribute('href') || imgElement.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (href && !href.startsWith('data:')) {
          try {
            // Load image and convert to data URL
            const dataUrl = await new Promise<string>((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth || img.width;
                canvas.height = img.naturalHeight || img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(img, 0, 0);
                  resolve(canvas.toDataURL('image/png'));
                } else {
                  reject(new Error('Could not get canvas context'));
                }
              };
              img.onerror = (e) => reject(new Error(`Failed to load image: ${href}`));
              img.src = href;
            });
            imgElement.setAttribute('href', dataUrl);
            imgElement.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', dataUrl);
          } catch (error) {
            console.error('Failed to convert image to data URL:', error);
            throw error; // Throw error to prevent export with broken images
          }
        }
      });
      
      await Promise.all(imagePromises);
      
      // Remove the transform from cloned ad-group to position it at 0,0
      clonedAdGroup.removeAttribute('transform');
      tempSvg.appendChild(clonedAdGroup);
      
      // Serialize the SVG
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(tempSvg);
      
      // Convert to blob using canvas
      return new Promise<Blob>((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        const img = new Image();
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          URL.revokeObjectURL(url);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/png');
        };
        
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG'));
        };
        
        img.src = url;
      });
    }
  }));
  
  const dragStateRef = useRef<{
    elementId: string | null;
    mode: 'move' | 'resize' | 'rotate' | null;
    startX: number;
    startY: number; 
    initialElement: AdElement | null;
  }>({
    elementId: null,
    mode: null,
    startX: 0,
    startY: 0,
    initialElement: null
  });

  // Zoom controls handlers
  const handleZoomIn = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.scaleBy as any, 1.3);
  };

  const handleZoomOut = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.scaleBy as any, 0.7);
  };

  const handleZoomReset = () => {
    if (!svgRef.current || !zoomRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.transition().duration(300).call(zoomRef.current.transform as any, d3.zoomIdentity);
  };

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    
    // Setup zoom behavior on the SVG (not on ad-group)
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 5])
      .on('zoom', (event) => {
        transformRef.current = event.transform;
        svg.select('g.zoom-group').attr('transform', event.transform.toString());
      });

    zoomRef.current = zoom;
    svg.call(zoom as any);

    // Clear existing content in zoom group
    svg.select('g.zoom-group').selectAll('*').remove();
    const zoomGroup = svg.select('g.zoom-group');

    // Calculate center position for ad group
    const adGroupX = (canvasWidth - width) / 2;
    const adGroupY = (canvasHeight - height) / 2;

    // Create ad group container that changes size based on platform
    const adGroup = zoomGroup.append('g')
      .attr('class', 'ad-group')
      .attr('transform', `translate(${adGroupX}, ${adGroupY})`);

    // Add border around ad area
    adGroup.append('rect')
      .attr('class', 'ad-border')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#ffffff')
      .attr('stroke', '#4169E1')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5');

    // Render background image inside ad group
    if (backgroundImage) {
      adGroup.append('image')
        .attr('class', 'background')
        .attr('href', backgroundImage)
        .attr('width', width)
        .attr('height', height)
        .attr('preserveAspectRatio', 'xMidYMid slice');
    }

    // Render elements inside ad group
    elements.forEach(element => {
      const textAlign = element.textAlign || 'center';
      const justifyMap = { left: 'flex-start', center: 'center', right: 'flex-end' };
      
      const group = adGroup.append('g')
        .attr('class', `element-group ${element.id === selectedElementId ? 'selected' : ''}`)
        .attr('data-element-id', element.id)
        .attr('transform', `translate(${element.x}, ${element.y}) rotate(${element.rotation}, ${element.width / 2}, ${element.height / 2})`);

      // Render based on type using foreignObject
      if (element.type === 'text') {
        const fo = group.append('foreignObject')
          .attr('width', element.width)
          .attr('height', element.height);

        fo.append('xhtml:div')
          .attr('class', 'text-content-wrapper')
          .style('width', '100%')
          .style('height', '100%')
          .style('display', 'flex')
          .style('align-items', 'center')
          .style('justify-content', justifyMap[textAlign])
          .style('padding', '8px')
          .style('box-sizing', 'border-box')
          .style('background', 'rgba(255, 255, 255, 0.05)')
          .style('border', element.id === selectedElementId ? '2px solid #4169E1' : '2px solid transparent')
          .style('border-radius', '4px')
          .style('overflow', 'hidden')
          .style('word-wrap', 'break-word')
          .style('font-size', `${element.fontSize || 16}px`)
          .style('font-family', element.fontFamily || 'Arial')
          .style('color', element.fill || '#000000')
          .style('text-align', textAlign)
          .style('line-height', '1.4')
          .html(element.content || '');
      } else if (element.type === 'image') {
        group.append('image')
          .attr('href', element.imageUrl || '')
          .attr('width', element.width)
          .attr('height', element.height)
          .attr('preserveAspectRatio', 'xMidYMid meet');
          
        if (element.id === selectedElementId) {
          group.append('rect')
            .attr('width', element.width)
            .attr('height', element.height)
            .attr('fill', 'none')
            .attr('stroke', '#4169E1')
            .attr('stroke-width', 2);
        }
      } else if (element.type === 'rect') {
        const fo = group.append('foreignObject')
          .attr('width', element.width)
          .attr('height', element.height);

        fo.append('xhtml:div')
          .attr('class', 'cta-button-wrapper')
          .style('width', '100%')
          .style('height', '100%')
          .style('display', 'flex')
          .style('align-items', 'center')
          .style('justify-content', 'center')
          .style('background', element.fill || '#4169E1')
          .style('border-radius', '5px')
          .style('border', element.id === selectedElementId ? '2px solid #FFF' : 'none')
          .style('box-shadow', element.id === selectedElementId ? '0 0 0 3px rgba(65, 105, 225, 0.8)' : 'none')
          .style('cursor', 'pointer')
          .style('font-size', `${element.fontSize || 16}px`)
          .style('font-family', element.fontFamily || 'Arial')
          .style('color', '#FFFFFF')
          .style('font-weight', 'bold')
          .style('text-align', 'center')
          .html(element.content || '');
      }

      // Add invisible interaction layer for drag
      group.append('rect')
        .attr('class', 'interaction-layer')
        .attr('width', element.width)
        .attr('height', element.height)
        .attr('fill', 'transparent')
        .style('cursor', 'move')
        .on('click', (event) => {
          event.stopPropagation();
          onElementSelect(element.id);
        })
        .call(
          d3.drag<SVGRectElement, unknown>()
            .on('start', function(event) {
              const [startX, startY] = d3.pointer(event.sourceEvent, adGroup.node());
              
              dragStateRef.current = {
                elementId: element.id,
                mode: 'move',
                startX: startX,
                startY: startY,
                initialElement: { ...element }
              };
            })
            .on('drag', function(event) {
              if (!dragStateRef.current.initialElement) return;
              
              const [currentX, currentY] = d3.pointer(event.sourceEvent, adGroup.node());
              const dx = currentX - dragStateRef.current.startX;
              const dy = currentY - dragStateRef.current.startY;
              
              onElementUpdate(element.id, {
                x: dragStateRef.current.initialElement.x + dx,
                y: dragStateRef.current.initialElement.y + dy
              });
            })
            .on('end', function() {
              dragStateRef.current = {
                elementId: null,
                mode: null,
                startX: 0,
                startY: 0,
                initialElement: null
              };
            })
        );

      // Add resize and rotate handles for selected element
      if (element.id === selectedElementId) {
        const handlesGroup = adGroup.append('g')
          .attr('class', 'handles')
          .attr('transform', `translate(${element.x}, ${element.y}) rotate(${element.rotation}, ${element.width / 2}, ${element.height / 2})`);

        // Resize handles (corners)
        const handles = [
          { x: 0, y: 0, cursor: 'nwse-resize', type: 'nw' },
          { x: element.width, y: 0, cursor: 'nesw-resize', type: 'ne' },
          { x: element.width, y: element.height, cursor: 'nwse-resize', type: 'se' },
          { x: 0, y: element.height, cursor: 'nesw-resize', type: 'sw' }
        ];

        handles.forEach(handle => {
          handlesGroup.append('circle')
            .attr('class', `resize-handle resize-${handle.type}`)
            .attr('cx', handle.x)
            .attr('cy', handle.y)
            .attr('r', 8)
            .attr('fill', '#4169E1')
            .attr('stroke', '#FFFFFF')
            .attr('stroke-width', 2)
            .style('cursor', handle.cursor)
            .call(
              d3.drag<SVGCircleElement, unknown>()
                .on('start', function(event) {
                  const [startX, startY] = d3.pointer(event.sourceEvent, adGroup.node());
                  
                  dragStateRef.current = {
                    elementId: element.id,
                    mode: 'resize',
                    startX: startX,
                    startY: startY,
                    initialElement: { ...element }
                  };
                })
                .on('drag', function(event) {
                  if (!dragStateRef.current.initialElement) return;

                  const [currentX, currentY] = d3.pointer(event.sourceEvent, adGroup.node());
                  const dx = currentX - dragStateRef.current.startX;
                  const dy = currentY - dragStateRef.current.startY;

                  let newWidth = dragStateRef.current.initialElement.width;
                  let newHeight = dragStateRef.current.initialElement.height;
                  let newX = dragStateRef.current.initialElement.x;
                  let newY = dragStateRef.current.initialElement.y;

                  if (handle.type === 'se') {
                    newWidth = Math.max(50, dragStateRef.current.initialElement.width + dx);
                    newHeight = Math.max(20, dragStateRef.current.initialElement.height + dy);
                  } else if (handle.type === 'sw') {
                    newWidth = Math.max(50, dragStateRef.current.initialElement.width - dx);
                    newHeight = Math.max(20, dragStateRef.current.initialElement.height + dy);
                    newX = dragStateRef.current.initialElement.x + dx;
                  } else if (handle.type === 'ne') {
                    newWidth = Math.max(50, dragStateRef.current.initialElement.width + dx);
                    newHeight = Math.max(20, dragStateRef.current.initialElement.height - dy);
                    newY = dragStateRef.current.initialElement.y + dy;
                  } else if (handle.type === 'nw') {
                    newWidth = Math.max(50, dragStateRef.current.initialElement.width - dx);
                    newHeight = Math.max(20, dragStateRef.current.initialElement.height - dy);
                    newX = dragStateRef.current.initialElement.x + dx;
                    newY = dragStateRef.current.initialElement.y + dy;
                  }

                  onElementUpdate(element.id, {
                    width: newWidth,
                    height: newHeight,
                    x: newX,
                    y: newY
                  });
                })
                .on('end', function() {
                  dragStateRef.current = {
                    elementId: null,
                    mode: null,
                    startX: 0,
                    startY: 0,
                    initialElement: null
                  };
                })
            );
        });

        // Rotation handle (top center)
        handlesGroup.append('line')
          .attr('class', 'rotation-line')
          .attr('x1', element.width / 2)
          .attr('y1', 0)
          .attr('x2', element.width / 2)
          .attr('y2', -30)
          .attr('stroke', '#4169E1')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4');

        handlesGroup.append('circle')
          .attr('class', 'rotation-handle')
          .attr('cx', element.width / 2)
          .attr('cy', -30)
          .attr('r', 10)
          .attr('fill', '#10B981')
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', 2)
          .style('cursor', 'grab')
          .call(
            d3.drag<SVGCircleElement, unknown>()
              .on('start', function(event) {
                d3.select(this).style('cursor', 'grabbing');
                const [startX, startY] = d3.pointer(event.sourceEvent, adGroup.node());
                
                dragStateRef.current = {
                  elementId: element.id,
                  mode: 'rotate',
                  startX: startX,
                  startY: startY,
                  initialElement: { ...element }
                };
              })
              .on('drag', function(event) {
                if (!dragStateRef.current.initialElement) return;

                const [currentX, currentY] = d3.pointer(event.sourceEvent, adGroup.node());
                
                // Calculate rotation relative to element center
                const centerX = element.x + element.width / 2;
                const centerY = element.y + element.height / 2;

                const angle = Math.atan2(currentY - centerY, currentX - centerX) * (180 / Math.PI);
                const newRotation = (angle + 90) % 360;

                onElementUpdate(element.id, {
                  rotation: newRotation
                });
              })
              .on('end', function() {
                d3.select(this).style('cursor', 'grab');
                dragStateRef.current = {
                  elementId: null,
                  mode: null,
                  startX: 0,
                  startY: 0,
                  initialElement: null
                };
              })
          );
      }
    });

  }, [width, height, backgroundImage, elements, selectedElementId, onElementUpdate, onElementSelect, canvasWidth, canvasHeight]);

  return (
    <div ref={containerRef} className="svg-ad-canvas-container">
      <div className="zoom-controls">
        <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M10 4v12M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M4 10h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <button className="zoom-btn" onClick={handleZoomReset} title="Reset Zoom">
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path d="M10 2v3m0 10v3m6-8h3M2 10h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <svg
        ref={svgRef}
        width={canvasWidth}
        height={canvasHeight}
        className="svg-ad-canvas"
        onClick={() => onElementSelect('')}
      >
        <defs>
          <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(100,100,120,0.3)" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width={canvasWidth} height={canvasHeight} fill="url(#grid)" />
        <g className="zoom-group"></g>
      </svg>
    </div>
  );
});

SvgAdCanvas.displayName = 'SvgAdCanvas';

export default SvgAdCanvas;
